/**
 * CSS custom properties (variables) utilities
 * Provides runtime CSS variable management
 */

import { colors } from '../tokens/colors'
import { spacing, borderRadius } from '../tokens/spacing'
import { fontFamily, fontSize } from '../tokens/typography'

/**
 * Generate CSS custom properties for design tokens
 */
export const generateCSSVariables = (_mode: 'light' | 'dark' = 'light') => {
  const colorVars: Record<string, string> = {}

  // Generate color variables
  Object.entries(colors).forEach(([colorName, colorValue]) => {
    if (typeof colorValue === 'string') {
      colorVars[`--color-${colorName}`] = colorValue
    } else if (typeof colorValue === 'object') {
      Object.entries(colorValue).forEach(([shade, value]) => {
        colorVars[`--color-${colorName}-${shade}`] = value
      })
    }
  })

  // Generate spacing variables
  const spacingVars: Record<string, string> = {}
  Object.entries(spacing).forEach(([key, value]) => {
    spacingVars[`--spacing-${key}`] = value
  })

  // Generate border radius variables
  const radiusVars: Record<string, string> = {}
  Object.entries(borderRadius).forEach(([key, value]) => {
    radiusVars[`--radius-${key}`] = value
  })

  // Generate font family variables
  const fontVars: Record<string, string> = {}
  Object.entries(fontFamily).forEach(([key, value]) => {
    fontVars[`--font-${key}`] = Array.isArray(value) ? value.join(', ') : String(value)
  })

  // Generate font size variables
  const fontSizeVars: Record<string, string> = {}
  Object.entries(fontSize).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      fontSizeVars[`--font-size-${key}`] = value[0]
      if (value[1] && typeof value[1] === 'object' && 'lineHeight' in value[1]) {
        fontSizeVars[`--line-height-${key}`] = value[1].lineHeight
      }
    } else {
      fontSizeVars[`--font-size-${key}`] = String(value)
    }
  })

  return {
    ...colorVars,
    ...spacingVars,
    ...radiusVars,
    ...fontVars,
    ...fontSizeVars,
  }
}

/**
 * Apply CSS variables to document root
 */
export const applyCSSVariables = (mode: 'light' | 'dark' = 'light') => {
  if (typeof document === 'undefined') return

  const variables = generateCSSVariables(mode)
  const root = document.documentElement

  Object.entries(variables).forEach(([property, value]) => {
    root.style.setProperty(property, value)
  })
}

/**
 * Remove CSS variables from document root
 */
export const removeCSSVariables = () => {
  if (typeof document === 'undefined') return

  const variables = generateCSSVariables()
  const root = document.documentElement

  Object.keys(variables).forEach(property => {
    root.style.removeProperty(property)
  })
}

/**
 * Get CSS variable value
 */
export const getCSSVariable = (property: string): string => {
  if (typeof document === 'undefined') return ''

  return getComputedStyle(document.documentElement).getPropertyValue(property).trim()
}

/**
 * Set CSS variable value
 */
export const setCSSVariable = (property: string, value: string) => {
  if (typeof document === 'undefined') return

  document.documentElement.style.setProperty(property, value)
}

/**
 * CSS variable helper functions
 */
export const cssVar = {
  // Color helpers
  color: (name: string, shade?: string | number) => {
    const varName = shade ? `--color-${name}-${shade}` : `--color-${name}`
    return `var(${varName})`
  },

  // Spacing helpers
  spacing: (size: string | number) => `var(--spacing-${size})`,

  // Border radius helpers
  radius: (size: string) => `var(--radius-${size})`,

  // Font helpers
  font: (family: string) => `var(--font-${family})`,
  fontSize: (size: string) => `var(--font-size-${size})`,
  lineHeight: (size: string) => `var(--line-height-${size})`,

  // Generic helper
  get: (property: string) => `var(--${property})`,
}

/**
 * CSS-in-JS style object with CSS variables
 */
export const createCSSVariableStyles = (mode: 'light' | 'dark' = 'light') => {
  const variables = generateCSSVariables(mode)

  return {
    ':root': variables,
    '[data-theme="light"]': generateCSSVariables('light'),
    '[data-theme="dark"]': generateCSSVariables('dark'),
  }
}
